import React, { useCallback, useState, useMemo } from 'react'
import { Grid } from '@material-ui/core'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import InlineCardHeader from '../../../../../molecules/Card/InlineCardHeader'
import FieldDisplay from '../../../../../molecules/FieldDisplay'
import OpalCard from '../../../../../opal/OpalCard'
import SydButton from '../../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import Switch from '../../../../../molecules/Switch'
import { useModifyQueryBlueprint } from '../../../../../../api/denali'
import { decipherError } from '../../../../../../utils/decipherError'

const useSubmitter = (form, blueprint, onSave, setIsEditing) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { handleSubmit } = form
  const { mutateAsync: modifyQueryBlueprint } = useModifyQueryBlueprint()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      blueprintId: blueprint.blueprintId,
      name: formData.name,
      description: formData.description || null,
      enabled: formData.enabled
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyQueryBlueprint(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the query blueprint')
        return
      }
      setIsEditing(false)
      onSave(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [modifyQueryBlueprint, blueprint, onSave, setIsEditing])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function BlueprintInfoSection ({ blueprint, onSave, isEditing, setIsEditing }) {
  const defaultValues = useMemo(() => ({
    name: blueprint.name || '',
    description: blueprint.description || '',
    enabled: blueprint.enabled || false
  }), [blueprint])

  const form = useForm({
    defaultValues,
    mode: 'onChange'
  })

  const { submitter, processing, error } = useSubmitter(form, blueprint, onSave, setIsEditing)

  const handleEdit = useCallback(() => {
    setIsEditing(true)
  }, [setIsEditing])

  const handleCancel = useCallback(() => {
    form.reset(defaultValues)
    setIsEditing(false)
  }, [form, defaultValues, setIsEditing])

  const fields = useMemo(() => [
    { label: 'Blueprint ID', value: blueprint.blueprintId },
    { label: 'Code', value: blueprint.code },
    { label: 'Name', value: blueprint.name },
    { label: 'Description', value: blueprint.description || '-' },
    { label: 'Enabled', value: blueprint.enabled ? 'Yes' : 'No' },
    { label: 'Active Version', value: blueprint.definitionVersionId || 'None' }
  ], [blueprint])

  return (
    <OpalCard size='sm'>
      <InlineCardHeader
        action={
          !isEditing ? (
            <SydButton onClick={handleEdit} icon='edit'>Edit</SydButton>
          ) : null
        }
      >
        Blueprint Information
      </InlineCardHeader>

      {!isEditing ? (
        <FieldDisplay fields={fields} />
      ) : (
        <div style={{ padding: '16px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SydLabel label='Blueprint ID'>
                <SydInput value={blueprint.blueprintId} disabled />
              </SydLabel>
            </Grid>

            <Grid item xs={12} md={6}>
              <SydLabel label='Code'>
                <SydInput value={blueprint.code} disabled />
              </SydLabel>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                control={form.control}
                name='name'
                rules={{
                  required: { value: true, message: 'Required' },
                  maxLength: { value: 128, message: 'Maximum 128 Characters' }
                }}
                render={(f) => (
                  <SydLabel required label='Name' error={hookFormErrorAdapter(form, f.fieldState)}>
                    <SydInput {...f.field} />
                  </SydLabel>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                control={form.control}
                name='enabled'
                render={(f) => (
                  <SydLabel label='Enabled'>
                    <Switch
                      checked={f.field.value}
                      onChange={(e) => f.field.onChange(e.target.checked)}
                    />
                  </SydLabel>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={form.control}
                name='description'
                render={(f) => (
                  <SydLabel label='Description'>
                    <SydInput {...f.field} multiline rows={3} />
                  </SydLabel>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              {error ? <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div> : null}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <SydButton disabled={processing} variant='ghost' size='md' onClick={handleCancel}>
                  Cancel
                </SydButton>
                <SydButton
                  processing={processing}
                  disabled={!form.formState.isValid}
                  variant='primary'
                  size='md'
                  onClick={submitter}
                >
                  Save
                </SydButton>
              </div>
            </Grid>
          </Grid>
        </div>
      )}
    </OpalCard>
  )
}

BlueprintInfoSection.propTypes = {
  blueprint: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired
}

export default BlueprintInfoSection
